/**
 * Ein Zähltext, der bei eins die Einzahl nimmt — und die Zahl in der Sprache
 * der Anwendung formatiert.
 *
 * Der eigene `useTranslation`-Hook kennt keine Pluralregeln: er ersetzt nur
 * Platzhalter. Ohne Helfer entsteht an jeder Zählstelle aufs Neue „1
 * Wertpapiere", und genau das ist beim Bau der Merkliste dreimal passiert —
 * im Kopf, im Übernahme-Dialog und im Löschdialog.
 *
 * Der Helfer stand deshalb zuerst in `WatchlistPage.tsx`. Die
 * Anreicherungs-Seite und die Simulation zählen aber genauso („Kurse",
 * „Simulationen"), und eine zweite Fassung driftet beim ersten Sonderfall von
 * der ersten weg. Also eine Stelle für alle drei (Regel #1/#4), analog zu
 * `shared/isins.ts`.
 *
 * `{count}` wird IMMER über `toLocaleString(locale)` eingesetzt, nie roh: eine
 * Zahl wie 2417 gehört als „2.417" auf eine deutsche und als „2,417" auf eine
 * englische Oberfläche (Regel aus PR #208). Wer eine andere Formatierung
 * braucht, überschreibt `count` über `variablen` — der Spread steht bewusst
 * hinter dem Vorgabewert.
 *
 * Beide Zweige bekommen die Variablen. Die Einzahl-Fassung des Helfers ließ
 * sie zunächst weg; das ging nur so lange gut, wie kein Einzahl-Text einen
 * Platzhalter enthielt. „1 / 1 Simulation" enthält zwei.
 */
import { useLanguage } from '../hooks/useLanguage'
import { useTranslation } from '../hooks/useTranslation'
import { getLocale } from '../utils/locale'

export type Uebersetzer = (
  key: string,
  vorgabe: string,
  variablen?: Record<string, unknown>,
) => string

/**
 * Derselbe Zähltext ohne Hook — für Code, der ausserhalb einer Komponente
 * formuliert (etwa `befundText` der Datenprüfung, das dieselben Sätze für die
 * Liste und für die Zusammenfassung liefert). `useAnzahl` ist nur noch die
 * Fassung, die sich `t` und `locale` selbst holt.
 */
export function anzahlText(
  t: Uebersetzer,
  locale: string,
  anzahl: number,
  einzahlSchluessel: string,
  einzahlText: string,
  mehrzahlSchluessel: string,
  mehrzahlText: string,
  variablen: Record<string, unknown> = {},
): string {
  const werte = { count: anzahl.toLocaleString(locale), ...variablen }
  return anzahl === 1
    ? t(einzahlSchluessel, einzahlText, werte)
    : t(mehrzahlSchluessel, mehrzahlText, werte)
}

export function useAnzahl() {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const locale = getLocale(language)

  return (
    anzahl: number,
    einzahlSchluessel: string,
    einzahlText: string,
    mehrzahlSchluessel: string,
    mehrzahlText: string,
    variablen: Record<string, unknown> = {},
  ) => anzahlText(
    t, locale, anzahl,
    einzahlSchluessel, einzahlText, mehrzahlSchluessel, mehrzahlText, variablen,
  )
}
